import { api } from './api';
import type {
  ApiEnvelope, AuditFields, EngineListParams, PaginatedEnvelope,
} from './notification-engine.types';

const BASE = '/api/v1/action-masters';

/**
 * An action_master row — something a recipient can do about a notification.
 *
 * `effectKey` names a registered effect run on success, checked against a
 * compile-time allowlist: a key with no registered handler is refused at save
 * rather than failing when somebody presses the button. No effects are
 * registered yet, so leave it empty until one is.
 */
export interface ActionMaster extends AuditFields {
  _id: string;
  actionCode: string;
  description?: string | null;
  actionType: string;               // BUTTON | LINK | ACKNOWLEDGE | ...
  effectKey?: string | null;
  /** Gates who may take the action — an approval a viewer sees is not one they may press. */
  permissionKey?: string | null;
  isMemoRequired?: boolean;
  style?: Record<string, unknown> | null;
}

export type ActionMasterCreateRequest =
  Omit<ActionMaster, '_id' | keyof AuditFields> & { appId?: string | null };
export type ActionMasterUpdateRequest = Partial<ActionMasterCreateRequest>;

export const actionMastersService = {
  list: async (params?: EngineListParams): Promise<PaginatedEnvelope<ActionMaster>> => {
    const response = await api.get<PaginatedEnvelope<ActionMaster>>(BASE, { params });
    return response.data;
  },
  getById: async (id: string): Promise<ActionMaster> => {
    const response = await api.get<ApiEnvelope<ActionMaster>>(`${BASE}/${id}`);
    return response.data.data;
  },
  create: async (data: ActionMasterCreateRequest): Promise<ActionMaster> => {
    const response = await api.post<ApiEnvelope<ActionMaster>>(BASE, data);
    return response.data.data;
  },
  update: async (id: string, data: ActionMasterUpdateRequest): Promise<ActionMaster> => {
    const response = await api.put<ApiEnvelope<ActionMaster>>(`${BASE}/${id}`, data);
    return response.data.data;
  },
  /** Retire without deleting: the runtime reads only active rows. */
  deactivate: async (id: string): Promise<ActionMaster> => {
    const response = await api.post<ApiEnvelope<ActionMaster>>(`${BASE}/${id}/deactivate`);
    return response.data.data;
  },
  /** Put a deactivated action back in service. Re-validated on the way back in. */
  activate: async (id: string): Promise<ActionMaster> => {
    const response = await api.post<ApiEnvelope<ActionMaster>>(`${BASE}/${id}/activate`);
    return response.data.data;
  },
  softDelete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete<ApiEnvelope<{ message: string }>>(`${BASE}/${id}`);
    return response.data.data;
  },
  /**
   * The effect keys an action may name.
   *
   * An empty list is a real answer, not a failure — most actions have no
   * effect. On its own prefix rather than under this one, which already has a
   * `GET /{id}` that a literal sibling path would compete with.
   */
  effects: async (): Promise<string[]> => {
    const response = await api.get<ApiEnvelope<{ effects: string[] }>>(
      '/api/v1/notification-engine/registries/effects',
    );
    return response.data.data?.effects || [];
  },
};
